import React, { FC, useEffect, useRef, useState } from 'react';
import { styled } from 'components/ThemeConfigConnector/ThemeConfigConnnector';
import { ProgressIndicator } from 'components/ProgressIndicator/ProgressIndicator';
import { isNow } from 'utils/fnDate';

const UPDATE_INTERVAL_LIVE = 5000;
const UPDATE_INTERVAL_NOT_LIVE = 15000;

type PropTypes = {
    startTime?: number;
    endTime?: number;
    displayWithNullProgress?: boolean;
    onLiveStateChanged?: (boolean) => void;
};

export const LiveProgressIndicatorWrapper = styled.div``;

export const LiveProgressIndicator: FC<PropTypes> = ({
    startTime = -1,
    endTime = -1,
    displayWithNullProgress = false,
    onLiveStateChanged,
}) => {
    const progressUpdateInterval = useRef(0);
    const lastReportedLive = useRef<boolean | null>(null);
    const [progress, setProgress] = useState(0);

    const isLive = isNow(startTime * 1000, endTime * 1000);

    const getProgressInPercentage = (): number => {
        if (startTime === -1 || endTime === -1) {
            return 0;
        }

        const startTimeMs = startTime * 1000;
        const endTimeMs = endTime * 1000;
        const duration = endTimeMs - startTimeMs;
        const now = Date.now();

        if (now >= startTimeMs && now <= endTimeMs) {
            return (100 / duration) * (now - startTimeMs);
        }

        return 0;
    };

    useEffect(() => {
        setProgress(getProgressInPercentage());

        if (progressUpdateInterval.current) {
            clearInterval(progressUpdateInterval.current);
        }

        progressUpdateInterval.current = window.setInterval(
            () => {
                setProgress(getProgressInPercentage());
            },
            isLive ? UPDATE_INTERVAL_LIVE : UPDATE_INTERVAL_NOT_LIVE
        );

        return () => {
            if (progressUpdateInterval.current) {
                clearInterval(progressUpdateInterval.current);
            }
        };
    }, [startTime, endTime]);

    useEffect(() => {
        if (lastReportedLive.current === isLive) return;
        lastReportedLive.current = isLive;

        if (onLiveStateChanged) {
            onLiveStateChanged(isLive);
        }
    }, [isLive, onLiveStateChanged]);

    if (!isLive) return null;

    if (!displayWithNullProgress && progress < 1) {
        return null;
    }

    return (
        <LiveProgressIndicatorWrapper>
            <ProgressIndicator percentage={progress} displayWithNullProgress={displayWithNullProgress} />
        </LiveProgressIndicatorWrapper>
    );
};
